<template>
  <v-card outlined class="pa-5 sd-panel">
    <div class="d-flex flex-wrap align-center mb-3" style="gap: 4px 12px">
      <div class="text-subtitle-1 font-weight-bold">
        {{ $t("finance_settlement_detail") }} · {{ row.periodLabel }}
      </div>
      <v-spacer />
      <span class="sd-links">
        <a @click="$emit('print')">{{ $t("finance_download_pdf") }}</a>
        ·
        <a :class="{ 'sd-disabled': !overview }" @click="overview && exportCsvFile()">CSV</a>
      </span>
    </div>

    <v-progress-linear v-if="loading" indeterminate class="mb-3" />

    <statement-breakdown v-if="overview" :overview="overview" @line-click="openLines" />
    <v-alert v-else-if="!loading" type="info" text dense>
      {{ row.from ? $t("finance_breakdown_unavailable") : $t("finance_period_not_structured") }}
    </v-alert>

    <div class="sd-section mt-5">{{ $t("finance_payments_for_settlement") }}</div>
    <div v-if="row.paid > 0" class="sd-box">
      <div class="sd-pay">
        <span>{{ localeDate(row.date) }}</span>
        <span>{{ methodLabel }}</span>
        <span class="sd-note">{{ row.note || "—" }}</span>
        <strong :style="{ color: row.direction === 'to_restaurant' ? positive : negative }">
          {{ money(row.paid, currency) }}
        </strong>
      </div>
      <div v-if="row.createdBy" class="text-caption grey--text">
        {{ $t("finance_recorded_by") }}: {{ row.createdBy }}
      </div>
    </div>
    <div class="sd-box">
      <template v-if="row.paid <= 0">{{ $t("finance_no_payments_yet") }}</template>
      {{ $t("finance_expected") }} {{ row.expected === null ? "—" : money(row.expected, currency) }} ·
      {{ $t("finance_paid") }} {{ money(row.paid, currency) }} ·
      <strong>
        {{ $t("finance_open_amount") }}
        {{ row.expected === null ? "—" : money(Math.max(row.expected - row.paid, 0), currency) }}
      </strong>
    </div>

    <div class="mt-4">
      <a class="sd-report" :href="reportMailto">{{ $t("finance_report_error") }}</a>
    </div>

    <statement-lines-dialog
      v-model="linesOpen"
      :restaurant-id="restaurantId"
      :category="linesRow ? linesRow.key : ''"
      :label="linesRow ? linesRow.label : ''"
      :from="row.from || ''"
      :to="row.to || ''"
      :currency="currency"
      @open-order="$emit('open-order', $event)"
    />
  </v-card>
</template>

<script>
import restaurantFinanceService from "@/services/restaurantFinance";
import { exportCsv } from "@/utils/csvExport";
import StatementBreakdown from "../StatementBreakdown.vue";
import StatementLinesDialog from "../StatementLinesDialog.vue";
import hasFinanceContext from "../hasFinanceContext";
import { parseOverview } from "../overview";
import { FLOW_COLORS } from "../constants";

// Detalj izabranog obračuna: razrada za njegov period (klikabilna, kao na Zarada) + plaćanja.
// [SETTLEMENT] zamjenjuje izvor sa GET /settlements/{id}; prikaz ostaje isti.
export default {
  name: "SettlementDetailPanel",

  components: { StatementBreakdown, StatementLinesDialog },

  mixins: [hasFinanceContext],

  props: {
    row: { type: Object, required: true },
  },

  data: () => ({
    loading: false,
    overview: null,
    linesOpen: false,
    linesRow: null,
    seq: 0,
    positive: FLOW_COLORS.positive,
    negative: FLOW_COLORS.negative,
  }),

  computed: {
    currency() {
      return this.overview?.currency || this.restaurantCurrency;
    },
    methodLabel() {
      const method = this.row.method;
      if (!method) return "—";
      const key = `finance_method_${method}`;
      return this.$te(key) ? this.$t(key) : method;
    },
    reportMailto() {
      const subject = encodeURIComponent(`${this.$t("finance_report_error")}: ${this.row.periodLabel || ""}`);
      return `mailto:help@ordera.app?subject=${subject}`;
    },
  },

  watch: {
    "row.key": {
      immediate: true,
      handler: "load",
    },
  },

  methods: {
    async load() {
      this.overview = null;
      if (!this.row.from || !this.row.to || !this.restaurantId) return;
      const seq = ++this.seq;
      this.loading = true;
      try {
        const res = await restaurantFinanceService.getFinancialOverview(this.restaurantId, {
          from: this.row.from,
          to: this.row.to,
        });
        if (seq !== this.seq) return;
        this.overview = parseOverview(res.data?.data || res.data || {}, this.restaurantCurrency);
      } catch (error) {
        if (seq === this.seq) this.overview = null;
      } finally {
        if (seq === this.seq) this.loading = false;
      }
    },

    openLines(line) {
      this.linesRow = line;
      this.linesOpen = true;
    },

    exportCsvFile() {
      const o = this.overview;
      const rows = [];
      if (o.opening !== null) rows.push([this.$t("finance_opening_balance"), o.opening]);
      o.rows.forEach((r) => rows.push([r.label, r.value]));
      if (o.closing !== null) rows.push([this.$t("finance_closing_balance"), o.closing]);
      exportCsv(`obracun_${this.row.from}_${this.row.to}`, [this.$t("finance_description"), `${this.$t("finance_amount")} (${o.currency})`], rows);
    },
  },
};
</script>

<style scoped>
.sd-links {
  font-size: 0.82rem;
}

.sd-links a {
  cursor: pointer;
}

.sd-disabled {
  opacity: 0.4;
  pointer-events: none;
}

.sd-section {
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 6px;
}

.sd-box {
  background: #f3f3f1;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 0.86rem;
  margin-bottom: 6px;
}

.sd-pay {
  display: grid;
  grid-template-columns: 90px 90px 1fr auto;
  gap: 10px;
}

.sd-note {
  color: rgba(0, 0, 0, 0.6);
  word-break: break-word;
}

.sd-report {
  font-size: 0.85rem;
}
</style>
